import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, CheckCircle2, XCircle, CpuIcon, ChevronRight } from 'lucide-react';
import { Provider, MOCK_PROVIDERS, formatCurrency, getInitials, getProviderTotals } from '../data/providers';
export function ProvidersPage() {
  const [providers] = useState<Provider[]>(MOCK_PROVIDERS);
  const navigate = useNavigate();
  // Calculate global KPIs
  const kpis = useMemo(() => {
    const totalProviders = providers.length;
    const activeProviders = providers.filter(p => p.status === 'Active').length;
    let globalBudget = 0;
    let globalSpent = 0;
    providers.forEach(p => {
      const {
        totalBudget,
        totalSpent
      } = getProviderTotals(p);
      globalBudget += totalBudget;
      globalSpent += totalSpent;
    });
    const globalPercent = globalBudget > 0 ? Math.round(globalSpent / globalBudget * 100) : 0;
    return {
      totalProviders,
      activeProviders,
      globalBudget,
      globalSpent,
      globalPercent
    };
  }, [providers]);
  return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8" data-id="element-4167" data-mp="j9faawsthiy9">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8" data-id="element-4168" data-mp="jbjfrj6d6q0n">
        <h1 className="text-[28px] md:text-[32px] font-semibold text-[#1A202C] tracking-tight" data-id="element-4169" data-mp="x6vhiaixtgr2">
          Provider
        </h1>
        <button className="flex items-center gap-2 px-4 py-2.5 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors shadow-sm" data-id="element-4170" data-mp="5hin97609z8q">
          <Plus className="w-4 h-4" data-id="element-4171" data-mp="ks5mfejyvl7" />
          <span className="text-[13px] font-medium" data-id="element-4172" data-mp="ea3rli8w43e1">Aggiungi Provider</span>
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-4173" data-mp="9ikmvp3u4db8">
        {/* Card 1 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-4174" data-mp="3hydutfk3oggo">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-4175" data-mp="3dwsgrqvtxw4w">
            Provider Totali
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-4176" data-mp="1gwoc4pzpuquc">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-4177" data-mp="1lvsil8v9aagz">
              {kpis.totalProviders}
            </span>
          </div>
        </div>

        {/* Card 2 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-4178" data-mp="1vm40jzbryk6v">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-4179" data-mp="xyd4e2od6udf">
            Provider Attivi
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-4180" data-mp="12x6hvv3nildl">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-4181" data-mp="12xoljcsn81vj">
              {kpis.activeProviders}
            </span>
          </div>
        </div>

        {/* Card 3 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-4182" data-mp="1cwduuh6h4e06">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-4183" data-mp="2jawpvcxw5tsi">
            Budget Totale
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-4184" data-mp="2o97yxlgd409o">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-4185" data-mp="cnpb80e4mqg9">
              {formatCurrency(kpis.globalBudget)}
            </span>
          </div>
        </div>

        {/* Card 4 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-4186" data-mp="2x312lp6is4y">
          <div className="flex justify-between items-center mb-3" data-id="element-4187" data-mp="3u4fk769mhfni">
            <h3 className="text-[13px] font-medium text-gray-500" data-id="element-4188" data-mp="3u1tap1368q2n">
              Spesa Totale
            </h3>
            <span className="text-[11px] font-medium text-gray-400" data-id="element-4189" data-mp="3l390kobofoq1">
              {kpis.globalPercent}%
            </span>
          </div>
          <div className="mt-auto" data-id="element-4190" data-mp="3g57hi3e93pek">
            <div className="flex items-baseline gap-1 mb-2" data-id="element-4191" data-mp="3g4egzk5vxw4v">
              <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-4192" data-mp="3b63anjesu3re">
                {formatCurrency(kpis.globalSpent)}
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-4193" data-mp="19dotwnnzr5ck">
              <div className={`h-1.5 rounded-full ${kpis.globalPercent >= 80 ? 'bg-amber-500' : 'bg-[#00B4D8]'}`} style={{
              width: `${kpis.globalPercent}%`
            }} data-id="element-4194" data-mp="19ehs308vwmj3"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Provider List */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col divide-y divide-gray-100" data-id="element-4195" data-mp="2179w9cl8goz">
        {providers.map(provider => {
        const {
          totalBudget,
          totalSpent,
          percent
        } = getProviderTotals(provider);
        const isNearLimit = percent >= 80;
        return <button key={provider.id} onClick={() => navigate(`/providers/${provider.id}`)} className="w-full text-left p-4 md:p-5 hover:bg-gray-50 transition-colors group flex flex-col md:flex-row md:items-center gap-4 md:gap-6" data-id="element-4196" data-mp="3ahsfdg60grnc">
              {/* Logo & Name */}
              <div className="flex items-center gap-4 md:w-1/4 shrink-0" data-id="element-4197" data-mp="35heeqj6qojzc">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center border border-gray-100 shadow-sm bg-white overflow-hidden shrink-0" style={{
              backgroundColor: provider.logo ? 'white' : `${provider.color}15`
            }} data-id="element-4198" data-mp="2lvcm5ev0w3hx">
                  {provider.logo ? <img src={provider.logo} alt={provider.name} className="w-6 h-6 object-contain" data-id="element-4199" data-mp="34tx9qwxpgqqt" /> : <span className="text-[13px] font-bold" style={{
                color: provider.color
              }} data-id="element-4200" data-mp="3ejguk84zauob">
                      {getInitials(provider.name)}
                    </span>}
                </div>
                <div className="min-w-0" data-id="element-4201" data-mp="3jhs2uefmkx4f">
                  <h3 className="text-[14px] font-semibold text-[#1A202C] truncate group-hover:text-[#00B4D8] transition-colors" data-id="element-4202" data-mp="3qnl07nw08v51">
                    {provider.name}
                  </h3>
                  <div className="md:hidden mt-1" data-id="element-4203" data-mp="4g4lh3o6812k">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium ${provider.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`} data-id="element-4204" data-mp="4ejt9kpbwu7v">
                      {provider.status === 'Active' ? <CheckCircle2 className="w-2.5 h-2.5" data-id="element-4205" data-mp="n7dyvo12b9zf" /> : <XCircle className="w-2.5 h-2.5" data-id="element-4206" data-mp="s5p9w233fqj8" />}
                      {provider.status === 'Active' ? 'Attivo' : 'Inattivo'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Status (Desktop) */}
              <div className="hidden md:flex md:w-1/6 shrink-0" data-id="element-4207" data-mp="2mdt3utgwpro4">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium ${provider.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`} data-id="element-4208" data-mp="2mf46ur2j2ve6">
                  {provider.status === 'Active' ? <CheckCircle2 className="w-3 h-3" data-id="element-4209" data-mp="35b42qcymmj31" /> : <XCircle className="w-3 h-3" data-id="element-4210" data-mp="30cj1qbbjvvdj" />}
                  {provider.status === 'Active' ? 'Attivo' : 'Inattivo'}
                </span>
              </div>

              {/* Models */}
              <div className="flex items-center gap-1.5 md:w-1/6 shrink-0" data-id="element-4211" data-mp="2qdutu5da4wnk">
                <CpuIcon className="w-4 h-4 text-gray-400" data-id="element-4212" data-mp="1jykdcrlzazio" />
                <span className="text-[13px] text-gray-600" data-id="element-4213" data-mp="1jwptzhqcvfgp">
                  {provider.models.length}{' '}
                  {provider.models.length === 1 ? 'modello' : 'modelli'}
                </span>
              </div>

              {/* Budget */}
              <div className="flex-1 min-w-[200px]" data-id="element-4214" data-mp="29n801gsbiej5">
                <div className="flex items-center justify-between text-[12px] mb-1.5" data-id="element-4215" data-mp="29klhyn7swgmo">
                  <span className="font-medium text-[#1A202C]" data-id="element-4216" data-mp="1vozzkyw2ov38">
                    {formatCurrency(totalSpent)}
                  </span>
                  <span className="text-gray-500" data-id="element-4217" data-mp="2nemhhj6ku7o3">
                    su {formatCurrency(totalBudget)}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-4218" data-mp="2scxqxssymg5s">
                  <div className={`h-1.5 rounded-full transition-all ${isNearLimit ? 'bg-amber-500' : 'bg-[#00B4D8]'}`} style={{
                width: `${percent}%`
              }} data-id="element-4219" data-mp="2ekzt4e4p86ox" />
                </div>
              </div>

              {/* Chevron */}
              <div className="hidden md:flex shrink-0 pl-4" data-id="element-4220" data-mp="3b58gym2imjmh">
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#00B4D8] transition-colors" data-id="element-4221" data-mp="zo6kkbmphbds" />
              </div>
            </button>;
      })}
      </div>
    </div>;
}